import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import * as R from 'Router/routes';
import { Button, Link, Typography } from 'components';
import ChevronIcon from 'assets/icons/chevron-left.svg?react';
import { INTRO } from 'Router/routes';
import styles from './NotFound.module.scss';
import { useTranslation } from 'react-i18next';

type PSD2Status =
  | 'CASE_FINISHED'
  | 'FINISHED_WITHOUT_PAYMENT'
  | 'FINISHED_WITHOUT_PAYMENT_TRANSACTION_HISTORY';

export default function NotFound() {
  const navigate = useNavigate();
  const [psd2ErrorMsg, setPsd2ErrorMsg] = useState('');
  const [psd2CaseId, setPsd2CaseId] = useState('');
  const [psd2Status, setPsd2Status] = useState<PSD2Status>();
  const { t } = useTranslation();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorCode = urlParams.get('errorCode');
    const caseId = urlParams.get('caseId');
    const status = urlParams.get('status') as PSD2Status;

    if (status) setPsd2Status(status);
    if (caseId) setPsd2CaseId(caseId);
    if (errorCode) setPsd2ErrorMsg(errorCode);
  }, []);

  const handleRedirects = useCallback(() => {
    navigate(R.APPROVED);
  }, [navigate]);

  if (
    psd2CaseId &&
    (psd2ErrorMsg ||
      psd2Status === 'FINISHED_WITHOUT_PAYMENT' ||
      psd2Status === 'FINISHED_WITHOUT_PAYMENT_TRANSACTION_HISTORY')
  )
    return (
      <>
        <Helmet>
          <title>{t('not_found:html_title')}</title>
        </Helmet>

        <Typography
          kind="h1"
          size="xl"
          className={styles.title}
          dataTest="notFound-title"
        >
          {t('psd2_result:any_error.title')}
        </Typography>

        <Typography className={styles.description} dataTest="notFound-text">
          {t('psd2_result:any_error.text')}
        </Typography>

        <div>
          <Button
            className={styles.button}
            to={R.MANUAL_IDENTIFICATION}
            dataTest="notFound-btn"
          >
            {t('psd2_result:any_error.button')}
          </Button>
        </div>
      </>
    );

  if (psd2CaseId && !psd2ErrorMsg)
    return (
      <>
        <Helmet>
          <title>{t('not_found:html_title')}</title>
        </Helmet>

        <Typography kind="h1" size="xl" className={styles.title}>
          {t('psd2_result:no_error.title')}
        </Typography>

        <div>
          <Button className={styles.button} onClick={handleRedirects}>
            {t('psd2_result:no_error.button')}
          </Button>
        </div>
      </>
    );

  if (psd2CaseId && psd2ErrorMsg === 'ADEL_TIMEOUT')
    return (
      <>
        <Helmet>
          <title>{t('not_found:html_title')}</title>
        </Helmet>

        <Typography kind="h1" size="xl" className={styles.title}>
          {t('psd2_result:special_error.title')}
        </Typography>

        <Typography className={styles.description}>
          {t('psd2_result:special_error.text')}
        </Typography>

        <div>
          <Button className={styles.button} to={R.PSD2_BANKS}>
            {t('psd2_result:special_error.button')}
          </Button>
        </div>
      </>
    );

  return (
    <>
      <Helmet>
        <title>{t('not_found:html_title')}</title>
      </Helmet>

      <Typography kind="h1" size="xl" className={styles.title}>
        {t('not_found:title')}
      </Typography>

      <Typography className={styles.description}>
        {t('not_found:description')}
      </Typography>

      <div>
        <Button className={styles.button} to={INTRO}>
          {t('not_found:button')}
        </Button>
      </div>
      <div className={styles.link}>
        <Link external to={`${import.meta.env.VITE_HOMEPAGE_URL}`}>
          <ChevronIcon className={styles.chevron} />
          {t('not_found:link')}
        </Link>
      </div>
    </>
  );
}
